import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ErrorBanner, Spinner } from '../../../Admin/shared';
import { useReferredUser } from '../api/useReferredUser';
import { fmtAvgOut, fmtMinutes } from '../format';
import type { ReferredActivity, Visit } from '../api/types';
import { ENTERED_VIA_LABELS, STATUS_LABELS } from '../components/ReferralFilters';

/** `—` for a value we could not compute. Never `0` — that is a different claim. */
function fmtNum(v: number | null): string {
  return v === null ? '—' : v.toLocaleString();
}

/** A date/time that fails to parse (or is absent) renders `—`, never "Invalid Date". */
function fmtWhen(v: string | null): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

/** Recency only. `last_seen_at` is overwritten on every app open, so there is
 *  no count or frequency to show alongside it — a bare date, nothing more. */
function fmtLastSeenDate(v: string | null): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

/**
 * `display_name` can be null — fall back to the user id, never a blank or
 * the literal string "null".
 */
function userLabel(user: { display_name: string | null; user_id: string }): string {
  return user.display_name ?? user.user_id;
}

/**
 * `null` means the referrer account was DELETED — render "deleted account",
 * never a blank cell or an invented name. A present referrer can still have
 * a null `display_name`; fall back to the user id.
 */
function referrerLabel(referrer: { user_id: string; display_name: string | null } | null): string {
  if (referrer === null) return 'deleted account';
  return referrer.display_name ?? referrer.user_id;
}

/**
 * A null `invite_channel` means the channel was not recorded — distinct from
 * "no source." Settings-entered referrals ALWAYS have a null channel by
 * design. Matches `ReferralList`'s `channelCell` so the two views never
 * drift.
 */
function channelLabel(invite_channel: string | null, entered_via: 'signup' | 'settings'): string {
  if (invite_channel !== null) return invite_channel;
  return entered_via === 'settings' ? 'Not recorded (settings entry)' : 'Not recorded';
}

function entryLabel(entered_via: 'signup' | 'settings'): string {
  return ENTERED_VIA_LABELS[entered_via] ?? entered_via;
}

function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

/** null when the venue was soft-deleted; the visit still counts and still
 *  renders. */
function venueLabel(v: Visit): string {
  return v.business_name ?? 'deleted venue';
}

/**
 * `days_to_first_visit` is `first_visit_at::date - counted_at::date`, which is
 * NEGATIVE for a settings-entered referral by someone who was already visiting
 * before they redeemed a code. "-12 day(s) after referral" states a direction
 * its own sign contradicts, so render the magnitude with the word that matches.
 */
function firstVisitHint(days: number): string {
  if (days === 0) return 'same day as the referral';
  const magnitude = Math.abs(days).toLocaleString();
  return days < 0 ? `${magnitude} day(s) before referral` : `${magnitude} day(s) after referral`;
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-xl border border-gray-700 bg-gray-900 px-5 py-4">
      <p className="text-[11px] text-gray-500 font-medium">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-white tabular-nums tracking-tight">{value}</p>
      {hint && <p className="mt-1.5 text-[11px] text-gray-500">{hint}</p>}
    </div>
  );
}

/**
 * A null `minutes_out_avg` has two quite different causes. "No session has
 * closed" implies sessions existed and stayed open — which contradicts the
 * Visits stat sitting right next to it when that reads `0`. Say which it is.
 */
function avgOutHint(activity: ReferredActivity): string {
  if (activity.minutes_out_avg === null) {
    return activity.visits === 0 ? 'no visits to measure' : 'no session has closed';
  }
  return 'closed sessions only';
}

/**
 * The activity summary. `returned_to_app` must render as a yes/no plus the
 * `last_seen_at` date and NOTHING MORE — `users.last_seen_at` is overwritten
 * on every app open, so there is no count, frequency or curve behind it.
 * `return_days` is distinct calendar days with a visit, not a "days since"
 * figure and not a gap — labeled explicitly so it can't be misread either
 * way. `minutes_out_avg` null while `visits > 0` means no session ever
 * closed, and renders "—", never `0m`.
 */
function ActivitySummary({ activity }: { activity: ReferredActivity }): React.ReactElement {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <Stat label="Visits" value={fmtNum(activity.visits)} hint={`${fmtNum(activity.visits_verified)} verified`} />
      <Stat label="Venues visited" value={fmtNum(activity.venues_visited)} />
      <Stat label="Avg time out" value={fmtAvgOut(activity.minutes_out_avg)} hint={avgOutHint(activity)} />
      <Stat label="Total time out" value={fmtMinutes(activity.minutes_out_total)} hint="closed sessions only" />
      <Stat
        label="Return days"
        value={fmtNum(activity.return_days)}
        hint="distinct calendar days with a visit — not days since, not a gap"
      />
      <Stat
        label="First visit"
        value={fmtWhen(activity.first_visit_at)}
        hint={
          activity.first_visit_at === null
            ? 'never visited'
            : activity.days_to_first_visit !== null
              ? firstVisitHint(activity.days_to_first_visit)
              : undefined
        }
      />
      <Stat label="Friends" value={fmtNum(activity.friends)} />
      <Stat
        label="Returned to app"
        value={activity.returned_to_app ? 'Yes' : 'No'}
        hint={`last seen ${fmtLastSeenDate(activity.last_seen_at)}`}
      />
    </div>
  );
}

/**
 * Per-referred-user drill-down: who referred them and how, their lifetime
 * activity summary, their venue visit history, and their friends. Reached
 * from `ReferralList`'s "referred" column link — the route below must match
 * that link (`/analytics/referrals/referred/:userId`) exactly, or every link
 * to this page 404s.
 */
export function ReferredUserPage(): React.ReactElement {
  const { userId } = useParams<{ userId: string }>();
  const { data, loading, error } = useReferredUser(userId ?? null);

  // The backend 404s with `{"error": "referred_user_not_found"}`, and
  // `adminApi.ts` puts that string straight into `ApiError.message` (there is
  // no separate `code` surfaced through `useReferredUser`'s plain-string
  // `error`) — so this is the literal value to branch on, not a status code
  // or a parsed shape. A genuine network/server failure produces some other
  // message and must keep rendering the ErrorBanner below.
  const notFound = error === 'referred_user_not_found';

  // Every non-data state — not found, a genuine 500/network failure, and the
  // initial load — has no looked-up identity to name. The route-param UUID is
  // NOT one: rendering it as the heading claims a user was found and named.
  const viewedUserLabel = data ? userLabel(data.user) : notFound ? 'No referral record' : 'Referred user';

  return (
    <div className="min-h-screen bg-gray-950">
      <div className="max-w-5xl mx-auto px-6 py-8 space-y-5">
        <div>
          <Link to="/analytics/referrals" className="text-xs text-gray-500 hover:text-gray-300 transition-colors">
            ← Back to referral analytics
          </Link>
        </div>

        <header>
          <h1 className="text-xl font-semibold text-white tracking-tight">{viewedUserLabel}</h1>
          {data?.user.email && <p className="mt-1 text-xs text-gray-500">{data.user.email}</p>}
        </header>

        {notFound ? (
          <section className="rounded-2xl border border-gray-700 bg-gray-900 overflow-hidden">
            <p className="px-5 py-10 text-center text-sm text-gray-500">
              No referral record for this user. They may have been deleted, or were never referred.
            </p>
          </section>
        ) : (
          error && <ErrorBanner message={error} />
        )}
        {loading && !data && (
          <div className="flex justify-center py-24">
            <Spinner />
          </div>
        )}

        {data && (
          <>
            {/* Covers the whole page, not just the stat grid: the Visits and
                Friends tables below are equally lifetime, and this note has to
                survive the `activity === null` branch too. */}
            <p className="text-[11px] text-gray-500">
              Lifetime activity — never windowed, unaffected by any date range applied on the referrals list.
            </p>

            <section className="rounded-2xl border border-gray-700 bg-gray-900 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-700">
                <h2 className="text-sm font-semibold text-gray-200">Referral context</h2>
                <p className="mt-1 text-[11px] text-gray-500">Who referred this user in, and how.</p>
              </div>
              <div className="px-5 py-4 flex flex-wrap items-start gap-x-8 gap-y-4 text-xs">
                <div>
                  <p className="text-[11px] text-gray-500 font-medium">Referrer</p>
                  {data.referral.referrer?.user_id ? (
                    <Link
                      to={`/analytics/referrals/users/${data.referral.referrer.user_id}`}
                      className="mt-1 block text-gray-200 hover:text-violet-300 transition-colors font-medium"
                    >
                      {referrerLabel(data.referral.referrer)}
                    </Link>
                  ) : (
                    <p className="mt-1 italic text-gray-500">{referrerLabel(data.referral.referrer)}</p>
                  )}
                </div>
                <div>
                  <p className="text-[11px] text-gray-500 font-medium">Code</p>
                  <p className="mt-1 text-gray-400">{data.referral.code ?? '—'}</p>
                </div>
                <div>
                  <p className="text-[11px] text-gray-500 font-medium">Entry</p>
                  <p className="mt-1 text-gray-400">{entryLabel(data.referral.entered_via)}</p>
                </div>
                <div>
                  <p className="text-[11px] text-gray-500 font-medium">Channel</p>
                  <p className="mt-1 text-gray-400">
                    {channelLabel(data.referral.invite_channel, data.referral.entered_via)}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] text-gray-500 font-medium">Status</p>
                  <p className="mt-1 text-gray-400">{statusLabel(data.referral.status)}</p>
                </div>
                <div>
                  <p className="text-[11px] text-gray-500 font-medium">When</p>
                  <p className="mt-1 text-gray-400 whitespace-nowrap">{fmtWhen(data.referral.counted_at)}</p>
                </div>
              </div>
            </section>

            {data.activity === null ? (
              <section className="rounded-2xl border border-gray-700 bg-gray-900 overflow-hidden">
                <div className="px-5 py-10 text-center text-sm italic text-gray-500">
                  Activity is not available for this user.
                </div>
              </section>
            ) : (
              <ActivitySummary activity={data.activity} />
            )}

            <section className="rounded-2xl border border-gray-700 bg-gray-900 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-700">
                <h2 className="text-sm font-semibold text-gray-200">Visits</h2>
                <p className="mt-1 text-[11px] text-gray-500">
                  This user&apos;s check-in history, most recent first.
                </p>
              </div>

              {data.visits_truncated && (
                <div className="mx-5 mt-4 flex items-start gap-2 p-3 bg-amber-950/40 border border-amber-800/50 rounded-xl">
                  <svg
                    className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 9v3.75m0 3.75h.008M10.29 3.86l-8.18 14.16A1.5 1.5 0 003.42 20.5h17.16a1.5 1.5 0 001.31-2.48L13.71 3.86a1.5 1.5 0 00-2.42 0z"
                    />
                  </svg>
                  <p className="text-sm text-amber-300">
                    Only the most recent 500 visits are listed.
                  </p>
                </div>
              )}

              {data.visits.length === 0 ? (
                <p className="px-5 py-10 text-center text-sm text-gray-500">
                  {data.activity === null
                    ? 'Visit history is not available for this user.'
                    : 'Never visited a venue.'}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className="px-5 py-3 text-left font-semibold text-[11px] uppercase tracking-wider text-gray-500">
                          Venue
                        </th>
                        <th className="px-5 py-3 text-left font-semibold text-[11px] uppercase tracking-wider text-gray-500">
                          Start
                        </th>
                        <th className="px-5 py-3 text-left font-semibold text-[11px] uppercase tracking-wider text-gray-500">
                          Duration
                        </th>
                        <th className="px-5 py-3 text-left font-semibold text-[11px] uppercase tracking-wider text-gray-500">
                          Verified
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.visits.map((visit, i) => (
                        <tr
                          key={`${visit.business_id ?? 'deleted-venue'}-${visit.session_start}-${i}`}
                          className="border-b border-gray-800 last:border-b-0 hover:bg-gray-800/40 transition-colors"
                        >
                          <td className="px-5 py-3 text-gray-200 font-medium">
                            {visit.business_name === null ? (
                              <span className="italic text-gray-500">{venueLabel(visit)}</span>
                            ) : (
                              venueLabel(visit)
                            )}
                          </td>
                          <td className="px-5 py-3 text-gray-400 whitespace-nowrap">{fmtWhen(visit.session_start)}</td>
                          <td
                            className="px-5 py-3 text-gray-400"
                            title={visit.duration_minutes === null ? 'session never closed' : undefined}
                          >
                            {fmtMinutes(visit.duration_minutes)}
                          </td>
                          <td className="px-5 py-3 text-gray-400">{visit.verified ? 'Yes' : 'No'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="rounded-2xl border border-gray-700 bg-gray-900 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-700">
                <h2 className="text-sm font-semibold text-gray-200">Friends</h2>
                <p className="mt-1 text-[11px] text-gray-500">People this user is friends with on the app.</p>
              </div>

              {data.friends.length === 0 ? (
                <p className="px-5 py-10 text-center text-sm text-gray-500">No friends on record.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className="px-5 py-3 text-left font-semibold text-[11px] uppercase tracking-wider text-gray-500">
                          Friend
                        </th>
                        <th className="px-5 py-3 text-left font-semibold text-[11px] uppercase tracking-wider text-gray-500">
                          Friends since
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.friends.map((friend, i) => (
                        <tr
                          key={`${friend.user_id ?? 'deleted'}-${friend.friends_since}-${i}`}
                          className="border-b border-gray-800 last:border-b-0 hover:bg-gray-800/40 transition-colors"
                        >
                          <td className="px-5 py-3">
                            {friend.user_id ? (
                              <Link
                                to={`/analytics/friends/${friend.user_id}`}
                                className="text-gray-200 hover:text-violet-300 transition-colors font-medium"
                              >
                                {friend.display_name ?? friend.user_id}
                              </Link>
                            ) : (
                              <span className="italic text-gray-500">{friend.display_name ?? 'deleted account'}</span>
                            )}
                          </td>
                          <td className="px-5 py-3 text-gray-400 whitespace-nowrap">
                            {fmtWhen(friend.friends_since)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}

export default ReferredUserPage;
